import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  CollectionPreferences,
  Container,
  Header,
  Link,
  Pagination,
  Table,
} from '@amzn/awsui-components-react-v3';
import _ from 'lodash';
import React, { ReactElement, useState } from 'react';
import { CollectionPreferencesProps } from '@amzn/awsui-components-react-v3/uxdg';
import { useTranslation } from 'react-i18next';

type GetChildContainersResult = {
  containerId: string;
  containerName: string;
  containerType: string;
  status: string;
};

export function renderField(fieldName: string | number) {
  return (e: GetChildContainersResult): string | number => {
    return _.get(e, fieldName, '-');
  };
}

const ChildContainers = (props: {
  childContainers: any;
  loadingChildContainers: boolean;
}): ReactElement => {
  const { childContainers, loadingChildContainers } = props;
  const { t } = useTranslation();

  const columnDefinitions = [
    {
      id: 'containerName',
      header: 'ContainerName',
      cell: (item: GetChildContainersResult) => (
        <Link href={`/assets-inventory/container/${item.containerId}`}>{item.containerName}</Link>
      ),
      sortingField: 'containerName',
    },
    {
      id: 'containerType',
      header: 'ContainerType',
      cell: (item: GetChildContainersResult) => item.containerType || '-',
      sortingField: 'containerType',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: GetChildContainersResult) => item.status || '-',
      sortingField: 'status',
    },
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: ['containerName', 'containerType', 'status'],
  });

  const { items, collectionProps, paginationProps } = useCollection(childContainers, {
    pagination: { pageSize: preferences.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
  });

  return (
    <div>
      {items.length > 0 && (
        <Table
          {...collectionProps}
          columnDefinitions={columnDefinitions}
          items={items}
          header={<Header> {t('Child Containers')}</Header>}
          visibleColumns={preferences.visibleContent}
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: t('Next page'),
                previousPageLabel: t('Previous page'),
                pageLabel: (pageNum) => t(`Page ${pageNum} of all pages`),
              }}
            />
          }
          loading={loadingChildContainers}
          preferences={
            <CollectionPreferences
              title={t('Preferences')}
              onConfirm={({ detail }) => setPreferences(detail)}
              confirmLabel={t('Confirm')}
              cancelLabel={t('Cancel')}
              preferences={preferences}
              pageSizePreference={{
                title: t('Select page size'),
                options: [
                  { value: 5, label: t('5 items') },
                  { value: 10, label: t('10 items') },
                  { value: 20, label: t('20 items') },
                  { value: 50, label: t('50 items') },
                  { value: 100, label: t('100 items') },
                ],
              }}
              visibleContentPreference={{
                title: t('Select visible content'),
                options: [
                  {
                    label: t('Main distribution properties'),
                    options: [
                      { id: 'containerName', label: t('ContainerName') },
                      { id: 'containerType', label: t('ContainerType') },
                      { id: 'status', label: t('Status') },
                    ],
                  },
                ],
              }}
            />
          }
        />
      )}
      {items.length === 0 && (
        <Container header={<Header> {t('Child Containers')}</Header>}>
          <Box color="text-status-inactive" textAlign="center">
            {t('No Child Containers to display')}
          </Box>
        </Container>
      )}
    </div>
  );
};

export default ChildContainers;
